import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/Button';
import { CopyButton } from '../components/CopyButton';
import { Disclaimer } from '../components/Disclaimer';
import { Skeleton } from '../components/Skeleton';
import { useRugCheck, useRugCheckRecord } from '../hooks/useRugCheck';
import {
  ApiClientError,
  type RugCheckRecordResponse,
  type RugCheckResponse,
} from '../lib/apiClient';
import { dexPairUrl } from '../lib/dexscreener';
import {
  RUG_CHAINS,
  RUG_RULES,
  guessChain,
  isRugChain,
  normalizeAddress,
  type RugChain,
  type RugGroup,
  type RugVerdict,
} from '../lib/rugCheck';
import base from './CrashBouncePage.module.css';
import styles from './RugCheckPage.module.css';

const HOUR_MS = 3_600_000;

const pct = (x: number | null, digits = 0) =>
  x === null ? '—' : `${(x * 100).toFixed(digits)}%`;
const usd = (x: number | null) =>
  x === null
    ? '—'
    : x >= 1_000_000
      ? `$${(x / 1_000_000).toFixed(1)}M`
      : x >= 1_000
        ? `$${(x / 1_000).toFixed(1)}k`
        : `$${x.toLocaleString(undefined, { maximumSignificantDigits: 3 })}`;
const price = (x: number | null) =>
  x === null
    ? '—'
    : `$${x.toLocaleString(undefined, { maximumSignificantDigits: 4, minimumSignificantDigits: 2 })}`;
const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
const age = (ms: number) => {
  const hours = Math.floor(ms / HOUR_MS);
  return hours < 48 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
};
const shortAddress = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

const CHAIN_OPTIONS = Object.entries(RUG_CHAINS) as Array<
  [RugChain, (typeof RUG_CHAINS)[RugChain]]
>;

const VERDICT_LABEL: Record<RugVerdict, string> = {
  collecting: 'Collecting',
  useful: 'Useful',
  weak: 'Weak',
};
// The badge colors reuse the paper-test ones: useful reads as passing.
const VERDICT_CLASS: Record<RugVerdict, string> = {
  collecting: base.collecting,
  useful: base.passing,
  weak: base.failing,
};
const GROUP_LABEL: Record<RugGroup, string> = {
  flagged: 'Serious flag',
  warnings: 'Warnings only',
  clean: 'No flags',
};

export function RugCheckPage() {
  const [params, setParams] = useSearchParams();
  const paramChain = params.get('chain');
  const chain = isRugChain(paramChain) ? paramChain : null;
  const address = chain ? normalizeAddress(chain, params.get('address') ?? '') : null;

  const [draftAddress, setDraftAddress] = useState(params.get('address') ?? '');
  const [draftChain, setDraftChain] = useState<RugChain>(chain ?? 'ethereum');
  const [formError, setFormError] = useState<string | null>(null);

  const lookup = useRugCheck(chain, address);
  const record = useRugCheckRecord();

  const onAddressChange = (value: string) => {
    setDraftAddress(value);
    setFormError(null);
    // Switch between the EVM chains and Solana when the format says so;
    // which EVM chain stays the reader's pick.
    const guess = guessChain(value);
    if (guess === 'solana' && draftChain !== 'solana') setDraftChain('solana');
    if (guess === 'ethereum' && draftChain === 'solana') setDraftChain('ethereum');
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeAddress(draftChain, draftAddress);
    if (!normalized) {
      setFormError(
        RUG_CHAINS[draftChain].evm
          ? 'Paste a token contract address: 0x followed by 40 characters.'
          : 'Paste a Solana token (mint) address.',
      );
      return;
    }
    setParams({ chain: draftChain, address: normalized });
  };

  const missRate = record.data?.summary.missRate ?? null;

  return (
    <div className={base.page}>
      <div className={base.heading}>
        <h1>Rug check</h1>
      </div>
      <p className={styles.lead}>
        Paste a token address to check its contract and market for common rug-pull red
        flags. It can find warning signs; it can&apos;t tell you a token is safe.
      </p>
      <Disclaimer
        summary="Red flags only — never a safety rating."
        text="Not financial advice. Rug Check reports what two public scanners (GoPlus Security and DEX Screener) find. Scanners miss rugs that happen by slow selling, by settings changed after the check, or by tricks they don't detect. A token with no flags can still go to zero. Nothing here touches your wallet."
      />

      <form className={base.card} onSubmit={onSubmit} aria-label="Check a token">
        <label className={styles.field}>
          <span>Token address</span>
          <input
            className={styles.addressInput}
            value={draftAddress}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="0x… or Solana mint"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            aria-invalid={formError !== null}
            aria-describedby={formError ? 'rug-form-error' : undefined}
          />
        </label>
        <div className={styles.row}>
          <label className={styles.field}>
            <span>Chain</span>
            <select
              value={draftChain}
              onChange={(e) => setDraftChain(e.target.value as RugChain)}
            >
              {CHAIN_OPTIONS.map(([id, spec]) => (
                <option key={id} value={id}>
                  {spec.label}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="primary" className={styles.submit}>
            Check
          </Button>
        </div>
        {formError && (
          <p id="rug-form-error" className={styles.error} role="alert">
            {formError}
          </p>
        )}
      </form>

      {lookup.isFetching && !lookup.data && (
        <>
          <p className="visually-hidden" role="status">
            Checking the token…
          </p>
          <Skeleton width="100%" height="12rem" />
        </>
      )}
      {lookup.isError && (
        <p className={styles.error} role="alert">
          {lookup.error instanceof ApiClientError
            ? lookup.error.message
            : 'The check couldn’t run right now. Try again shortly.'}
        </p>
      )}
      {lookup.data && <Report data={lookup.data} missRate={missRate} />}

      <TrackRecord query={record} />

      <a
        className={styles.powered}
        href="https://gopluslabs.io"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className={styles.logoBadge}>
          {/* GoPlus's own logo, unaltered, as its API license asks. */}
          <img src="/brand/goplus-logo.png" alt="" width={93} height={20} />
        </span>
        Powered by GoPlus Security
      </a>
      <p className={base.fine}>
        Contract checks by GoPlus Security; market data from{' '}
        <a href="https://dexscreener.com" target="_blank" rel="noopener noreferrer">
          DEX Screener
        </a>
        . Both are public and called without keys. The first lookup of each token is kept
        (the token, its flags, liquidity and price; nothing about you) and checked again
        after 30 days for the track record, counting lookups from{' '}
        {dayTime(RUG_RULES.registeredAtMs)}.
      </p>
    </div>
  );
}

function Report({ data, missRate }: { data: RugCheckResponse; missRate: number | null }) {
  const r = data.report;
  const critical = r.flags.filter((f) => f.severity === 'critical');
  const warnings = r.flags.filter((f) => f.severity === 'warning');
  // Serious flags lead, whatever order they arrived in.
  const flags = [...critical, ...warnings];
  const m = r.market;
  const link = m ? dexPairUrl(RUG_CHAINS[r.chain].dex, m.pairAddress) : null;
  const headline =
    r.flags.length === 0
      ? 'None of these flags found'
      : `${r.flags.length} red flag${r.flags.length === 1 ? '' : 's'}${
          critical.length ? `, ${critical.length} serious` : ''
        }`;
  const tone = critical.length ? styles.bad : warnings.length ? styles.warn : styles.none;
  return (
    <section className={base.card} aria-labelledby="rug-result-heading">
      <div className={styles.tokenHead}>
        <div>
          <h2 id="rug-result-heading" className={styles.tokenName}>
            {r.name ?? 'Unknown token'}
            {r.symbol && <span className={styles.symbol}> {r.symbol}</span>}
          </h2>
          <p className={styles.addressLine}>
            {RUG_CHAINS[r.chain].label} ·{' '}
            <span className={styles.mono}>{shortAddress(r.address)}</span>{' '}
            <CopyButton value={r.address} label="Copy address" />
          </p>
        </div>
      </div>

      <p className={`${styles.headline} ${tone}`}>{headline}</p>

      {r.flags.length > 0 && (
        <ul className={styles.flags}>
          {flags.map((f) => (
            <li key={f.id} className={styles.flag}>
              <span
                className={`${styles.severity} ${f.severity === 'critical' ? styles.bad : styles.warn}`}
              >
                {f.severity === 'critical' ? 'Serious' : 'Warning'}
              </span>
              <span>{f.label}</span>
            </li>
          ))}
        </ul>
      )}

      {r.flags.length === 0 && (
        <p className={base.small}>
          That isn&apos;t a safety rating. Scanners miss slow rugs (a team selling over
          days) and anything changed after today.
          {missRate !== null &&
            ` So far, ${pct(missRate)} of tracked tokens that rugged showed no flags either.`}
        </p>
      )}

      {r.recognized && (
        <p className={base.small}>
          GoPlus lists this as a well-known token. The flags above still apply.
        </p>
      )}

      {r.unchecked.length > 0 && (
        <p className={base.small}>
          <strong>Couldn&apos;t check:</strong> {r.unchecked.join(', ')}.
        </p>
      )}

      {m && (
        <dl className={styles.market}>
          <div>
            <dt>Liquidity</dt>
            <dd>{usd(m.liquidityUsd)}</dd>
          </div>
          <div>
            <dt>Price</dt>
            <dd>{price(m.priceUsd)}</dd>
          </div>
          <div>
            <dt>Valuation</dt>
            <dd>{usd(m.fdv)}</dd>
          </div>
          <div>
            <dt>Pair age</dt>
            <dd>{m.createdMs === null ? '—' : age(r.checkedAtMs - m.createdMs)}</dd>
          </div>
        </dl>
      )}
      {m && link && (
        <p className={base.small}>
          Deepest pair: {m.dexId}
          {m.quoteSymbol ? ` / ${m.quoteSymbol}` : ''} ·{' '}
          <a href={link} target="_blank" rel="noopener noreferrer">
            View on DEX Screener
          </a>
        </p>
      )}

      <p className={base.small}>
        {r.checkedAtMs < RUG_RULES.registeredAtMs
          ? `The track record starts ${day(RUG_RULES.registeredAtMs)}; lookups before then aren’t kept.`
          : data.tracked
            ? 'Added to the track record. It gets checked again in 30 days.'
            : data.trackable
              ? 'This token is already in the track record, or tracking isn’t on here.'
              : `Not added to the track record: it needs a DEX pair with at least ${usd(RUG_RULES.minTrackedLiquidityUsd)} of liquidity.`}
      </p>
    </section>
  );
}

function TrackRecord({ query }: { query: ReturnType<typeof useRugCheckRecord> }) {
  const data: RugCheckRecordResponse | undefined = query.data;
  if (query.isLoading) return <Skeleton width="100%" height="8rem" />;
  if (!data) {
    return <p className={base.small}>The track record is temporarily unavailable.</p>;
  }
  const s = data.summary;
  const v = data.rules.verdict;
  return (
    <section className={base.card} aria-labelledby="rug-record-heading">
      <div className={base.cardHead}>
        <h2 id="rug-record-heading">Do the flags work?</h2>
        <span className={`${base.verdict} ${VERDICT_CLASS[s.verdict]}`}>
          {VERDICT_LABEL[s.verdict]}
        </span>
      </div>
      <p className={base.progress}>
        {s.resolved}/{v.minResolved} checked after 30 days · {s.rugs}/{v.minRugs} rugs
        {s.pending > 0 && ` · ${s.pending} waiting`}
      </p>
      {s.resolved > 0 && (
        <dl className={base.stats}>
          <div>
            <dt>Rugs that were flagged</dt>
            <dd>{pct(s.recall)}</dd>
          </div>
          <div>
            <dt>Rug rate, serious flag</dt>
            <dd>{pct(s.groups.flagged.rugRate)}</dd>
          </div>
          <div>
            <dt>Rug rate, no flags</dt>
            <dd>{pct(s.groups.clean.rugRate)}</dd>
          </div>
        </dl>
      )}
      <p className={base.small}>
        Every token looked up here is checked again after 30 days. It counts as a rug if
        its liquidity or price fell more than 90%, or its pairs are gone. The flags count
        as useful after {v.minResolved} tokens with at least {v.minRugs} rugs, if at least{' '}
        {pct(v.minRecall)} of the rugs had a serious flag and flagged tokens rugged at
        least {v.minLift}× as often as unflagged ones.
      </p>
      {data.resolved.length > 0 && (
        <ul className={base.list}>
          {data.resolved.slice(0, 20).map((t) => (
            <li key={`${t.chain}:${t.address}`} className={base.row}>
              <div className={base.rowHead}>
                <strong>
                  {t.symbol ?? shortAddress(t.address)}{' '}
                  <span className={styles.chainTag}>{RUG_CHAINS[t.chain].label}</span>
                </strong>
                <span className={t.outcome === 'rug' ? base.down : base.up}>
                  {t.outcome === 'rug' ? 'Rugged' : 'Survived'}
                </span>
              </div>
              <p className={base.small}>
                {GROUP_LABEL[t.group]} · checked {day(t.lookedUpMs)} · liquidity{' '}
                {usd(t.liquidityUsd)} → {usd(t.liquidityUsdLater)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
